import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/closeup-logo.png';
import bgImage from '../assets/pbg.png';
import { FaHeart, FaRegHeart, FaComment, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const translations = {
  en: {
    title: 'Stories',
    subtitle: 'Read inspiring stories from our community',
    noStories: 'No stories found. Be the first to share your story!',
    error: 'Error loading stories. Please try again.',
    loadMore: 'Load More Stories',
    anonymous: 'Anonymous',
    loading: 'Loading stories...',
    retryButton: 'Try Again'
  },
  si: {
    title: 'Stories',
    subtitle: 'අපගේ ප්‍රජාවගේ ආදරණීය කතා කියවන්න',
    noStories: 'කතා හමු නොවීය. පළමු කතාව බෙදා ගන්න!',
    error: 'කතා පූරණය කිරීමේ දෝෂයකි. නැවත උත්සාහ කරන්න.',
    loadMore: 'තවත් කතා පූරණය කරන්න',
    anonymous: 'නිර්නාමික',
    loading: 'Loading stories...',
    retryButton: 'නැවත උත්සාහ කරන්න'
  },
  ta: {
    title: 'கதைகள்',
    subtitle: 'எங்கள் சமூகத்தின் உத்வேகமான கதைகளைப் படியுங்கள்',
    noStories: 'கதைகள் எதுவும் கிடைக்கவில்லை. முதல் கதையைப் பகிரவும்!',
    error: 'கதைகளை ஏற்றுவதில் பிழை. மீண்டும் முயற்சிக்கவும்.',
    loadMore: 'மேலும் கதைகளை ஏற்றவும்',
    anonymous: 'அநாமதேயர்',
    loading: 'கதைகள் ஏற்றப்படுகின்றன...',
    retryButton: 'மீண்டும் முயற்சிக்கவும்'
  }
};

// Define all styled components first
const Container = styled.div`
height: 100vh;
  background: url(${bgImage}) no-repeat right center;
  background-size: cover;
  padding: 2rem;
  position: relative;
  overflow: scroll;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding: 0 1rem;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const Subtitle = styled.p`
  color: white;
  text-align: center;
  margin-bottom: 3rem;
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const Message = styled.div`
  color: white;
  text-align: center;
  padding: 2rem;
  background: ${props => props.error ? 'rgba(0, 0, 0, 0.3)' : 'transparent'};
  border-radius: 16px;
  margin: 2rem auto;
  max-width: 600px;
  font-size: 1.1rem;
  line-height: 1.6;
  border: ${props => props.error ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
  box-shadow: ${props => props.error ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none'};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const StoriesContainer = styled(motion.div)`
  width: 100%;
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0;
  }
`;

const StoryCard = styled.div`
  background: ${props => props.storyType === 'boot' ? '#d3d3d3' : '#eeeeee'};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 200px;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
  }
`;

const StoryText = styled.p`
  color: #333;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  flex-grow: 1;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StoryFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const AuthorName = styled.span`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ReactionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 20px;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.7 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    background: rgba(227, 24, 55, 0.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ReactionIcon = styled.img`
  width: 24px;
  height: 24px;
  transition: transform 0.2s;
  filter: ${props => props.disabled ? 'grayscale(100%)' : 'none'};
  
  ${ReactionButton}:hover & {
    transform: scale(1.1);
  }
`;

const ReactionCount = styled.span`
  color: #666;
  font-size: 1rem;
  font-weight: 600;
  min-width: 24px;
`;

const LoadingContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
`;

const RetryButton = styled.button`
  background: white;
  color: #e31837;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadMoreButton = styled.button`
  background: white;
  color: #e31837;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  margin: 2rem auto;
  display: block;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.7 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const CommentStats = styled.div`
  display: flex;
  align-items: center;
  color: #e31837;
  margin-right: 10px;
`;

const CommentCount = styled.span`
  margin-left: 5px;
  font-size: 0.9rem;
`;

const ViewButton = styled.button`
  background: #e31837;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #c01530;
  }
`;

const StoryDetailsPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
`;

const PopupContent = styled.div`
  background: white;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  border-radius: 10px;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #e31837;
  cursor: pointer;
  z-index: 100;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const StoryDetailCard = styled.div`
  background: #e31837;
  color: white;
  padding: 2rem;
  border-radius: 10px 10px 0 0;
`;

const StoryDetailText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  white-space: pre-wrap;
  margin-top: 1.5rem;
`;

const StoryDetailAuthor = styled.p`
  font-style: italic;
  margin-top: 1rem;
  text-align: right;
`;

const StoryActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  padding-top: 1rem;
`;

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 01);
  border: none;
  color: black;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 20px;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CommentCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  padding: 5px 10px;
`;

const CommentsSection = styled.div`
  padding: 2rem;
  background: white;
`;

const CommentsSectionTitle = styled.h3`
  color: #e31837;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const CommentForm = styled.form`
  margin-bottom: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  background: #f9f9f9;
`;

const CommentFormTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const CommentNameInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
  
  &:disabled {
    background: #eee;
    cursor: not-allowed;
  }
`;

const AnonymousOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
  resize: vertical;
`;

const CommentSubmitButton = styled.button`
  background: #e31837;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #c01530;
  }
  
  &:disabled {
    background: #999;
    cursor: not-allowed;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CommentItem = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
  background: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const CommentAuthor = styled.span`
  font-weight: bold;
  color: #e31837;
`;

const CommentDate = styled.span`
  color: #999;
`;

const CommentBody = styled.p`
  line-height: 1.5;
`;

const LoadingComments = styled.div`
  text-align: center;
  color: #999;
  padding: 1rem;
`;

const NoComments = styled.div`
  text-align: center;
  color: #999;
  padding: 1rem;
  font-style: italic;
`;

const NoStoriesContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const StoryContent = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const StoryAuthor = styled.p`
  font-style: italic;
  margin-top: 1rem;
  text-align: right;
  font-size: 0.9rem;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const StoriesList = ({ language = 'en' }) => {
  const t = translations[language];
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedStory, setSelectedStory] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentName, setCommentName] = useState('');
  const [commentText, setCommentText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  const loadStories = async (resetPage = true) => {
    try {
      setLoading(true);
      setError(null);

      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/story?operation=list`
        : `http://localhost:3001/api/story?operation=list`;

      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: resetPage ? 1 : page + 1,
          limit: 6,
          resetPage
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load stories');
      }

      const result = await response.json();

      if (!result.data) {
        setStories([]);
        setHasMore(false);
      } else {
        setStories(resetPage ? result.data : [...stories, ...result.data]);
        setHasMore(result.hasMore);
        if (!resetPage) setPage(page + 1);
      }
    } catch (err) {
      console.error('Error loading stories:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    loadStories();
  }, [language]); // Reload when language changes

  const handleRetry = () => {
    setPage(1);
    loadStories();
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadStories(false);
    }
  };

  const handleReaction = async (storyId) => {
    try {
      // Optimistic update of the UI
      const updatedStories = stories.map(story => {
        if (story.id === storyId) {
          return { ...story, likes_count: (story.likes_count || 0) + 1 };
        }
        return story;
      });
      setStories(updatedStories);
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/story?operation=like`
        : `http://localhost:3001/api/story?operation=like`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storyId,
          userName: commentName || null,
          isAnonymous
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to like story');
      }
      
      // If the story is currently selected, update it too
      if (selectedStory && selectedStory.id === storyId) {
        setSelectedStory({
          ...selectedStory,
          likes_count: (selectedStory.likes_count || 0) + 1
        });
      }
    } catch (error) {
      console.error('Error in handleReaction:', error);
      // Revert the optimistic update if there was an error
      loadStories(false);
    }
  };
  
  // New functions for comments feature
  const openStoryDetails = async (story) => {
    // Disable scrolling on the body when popup is open
    document.body.style.overflow = 'hidden';
    
    setSelectedStory(story);
    setLoadingComments(true);
    await loadComments(story.id);
  };
  
  const closeStoryDetails = () => {
    // Reset all popup-related state
    setSelectedStory(null);
    setComments([]);
    setCommentText('');
    setCommentName('');
    setIsAnonymous(false);
    setSubmittingComment(false);
    setLoadingComments(false);
    
    // Add a small delay to ensure the popup is fully closed before resetting state
    // This prevents flickering or UI glitches
    setTimeout(() => {
      document.body.style.overflow = 'auto'; // Re-enable scrolling on the body
    }, 100);
  };
  
  const loadComments = async (storyId) => {
    try {
      setLoadingComments(true);
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/story?operation=comments`
        : `http://localhost:3001/api/story?operation=comments`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storyId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load comments');
      }

      const result = await response.json();
      setComments(result.data || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };
  
  const handleLikeStory = async (storyId) => {
    try {
      // This function is no longer used - we're using handleReaction instead
      // Removing this function would be better, but for now let's comment it out
      /* 
      const { data, error } = await supabase
        .from('likes')
        .insert({
          story_id: storyId,
          user_name: commentName || null,
          is_anonymous: isAnonymous
        });
      */
    } catch (error) {
      console.error('Error in handleLikeStory:', error);
    }
  };
  
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim()) {
      return;
    }
    
    try {
      setSubmittingComment(true);
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/story?operation=comment`
        : `http://localhost:3001/api/story?operation=comment`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storyId: selectedStory.id,
          userName: isAnonymous ? null : commentName,
          commentText,
          isAnonymous
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit comment');
      }
      
      // Clear the form
      setCommentText('');
      
      // Reload comments
      loadComments(selectedStory.id);
      
      // Update the story in the UI
      setStories(prevStories =>
        prevStories.map(story =>
          story.id === selectedStory.id
            ? { ...story, comments_count: (story.comments_count || 0) + 1 }
            : story
        )
      );
      
      // Update selected story
      setSelectedStory(prev => ({
        ...prev,
        comments_count: (prev.comments_count || 0) + 1
      }));
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <Container>
      <Content>
        <LogoContainer>
          <Link to="/">
            <Logo src={logo} alt="Closeup Logo" />
          </Link>
        </LogoContainer>

        <Title>{t.title}</Title>
        {/* <Subtitle>{t.subtitle}</Subtitle> */}
        <AnimatePresence mode="wait">
          {error ? (
            <ErrorContainer
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Message error>{error}</Message>
              <RetryButton onClick={handleRetry}>{t.retryButton}</RetryButton>
            </ErrorContainer>
          ) : loading && stories.length === 0 ? (
            <LoadingContainer
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Message>{t.loading}</Message>
            </LoadingContainer>
          ) : stories.length === 0 ? (
            <NoStoriesContainer
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Message>{t.noStories}</Message>
            </NoStoriesContainer>
          ) : (
            <StoriesGrid
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {stories.map((story) => (
                <StoryCard
                  key={story.id}
                  storyType={story.story_type}
                  as={motion.div}
                  whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}
                >
                  <StoryContent>
                    <StoryText>{story.story}</StoryText>
                    <StoryAuthor>
                      {story.is_anonymous ? t.anonymous : story.name}
                    </StoryAuthor>
                  </StoryContent>
                  <StoryFooter>
                    <ReactionButton
                      onClick={() => handleReaction(story.id)}
                      disabled={false}
                    >
                      <FaHeart color="#e31837" size={20} />
                      <ReactionCount>{story.likes_count || 0}</ReactionCount>
                    </ReactionButton>
                    
                    <CommentStats>
                      <FaComment size={16} />
                      <CommentCount>{story.comments_count || 0}</CommentCount>
                    </CommentStats>
                    
                    <ViewButton onClick={() => openStoryDetails(story)}>
                      View
                    </ViewButton>
                  </StoryFooter>
                </StoryCard>
              ))}
            </StoriesGrid>
          )}
        </AnimatePresence>

        {!loading && stories.length > 0 && hasMore && (
          <LoadMoreContainer>
            <LoadMoreButton onClick={handleLoadMore}>
              {t.loadMore}
            </LoadMoreButton>
          </LoadMoreContainer>
        )}
      </Content>
      
      {/* Story Details Popup with Comments */}
      {selectedStory && (
        <StoryDetailsPopup>
          <PopupContent>
            <CloseButton onClick={closeStoryDetails} aria-label="Close popup">
              <FaTimes size={20} />
            </CloseButton>
            
            <StoryDetailCard>
              <StoryDetailText>{selectedStory.story}</StoryDetailText>
              <StoryDetailAuthor>
                {selectedStory.is_anonymous ? t.anonymous : selectedStory.name}
              </StoryDetailAuthor>
              
              <StoryActions>
                <LikeButton onClick={() => handleLikeStory(selectedStory.id)}>
                  <FaHeart size={20} color="#e31837" />
                  <span>{selectedStory.likes_count || 0}</span>
                </LikeButton>
                
                <CommentCounter>
                  <FaComment size={20} />
                  <span>{selectedStory.comments_count || 0}</span>
                </CommentCounter>
              </StoryActions>
            </StoryDetailCard>
            
            <CommentsSection>
              <CommentsSectionTitle>Comments</CommentsSectionTitle>
              
              <CommentForm onSubmit={handleSubmitComment}>
                <CommentFormTop>
                  <CommentNameInput 
                    type="text"
                    placeholder="Your Name (optional)"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    disabled={isAnonymous}
                  />
                  
                  <AnonymousOption>
                    <input 
                      type="checkbox" 
                      id="anonymous" 
                      checked={isAnonymous}
                      onChange={() => setIsAnonymous(!isAnonymous)}
                    />
                    <label htmlFor="anonymous">Comment Anonymously</label>
                  </AnonymousOption>
                </CommentFormTop>
                
                <CommentTextArea 
                  placeholder="Write a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  required
                />
                
                <CommentSubmitButton 
                  type="submit" 
                  disabled={submittingComment || !commentText.trim()}
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </CommentSubmitButton>
              </CommentForm>
              
              <CommentsList>
                {loadingComments ? (
                  <LoadingComments>Loading comments...</LoadingComments>
                ) : comments.length === 0 ? (
                  <NoComments>No comments yet. Be the first to comment!</NoComments>
                ) : (
                  comments.map(comment => (
                    <CommentItem key={comment.id}>
                      <CommentHeader>
                        <CommentAuthor>
                          {comment.is_anonymous ? 'Anonymous' : comment.user_name || 'Anonymous'}
                        </CommentAuthor>
                        <CommentDate>
                          {new Date(comment.created_at).toLocaleDateString()}
                        </CommentDate>
                      </CommentHeader>
                      <CommentBody>{comment.comment}</CommentBody>
                    </CommentItem>
                  ))
                )}
              </CommentsList>
            </CommentsSection>
          </PopupContent>
        </StoryDetailsPopup>
      )}
    </Container>
  );
};

export default StoriesList;
